import { useEffect } from 'react';
import { LinkId } from '../../../common/AnalyticsContext/LinkId';
import { TrackingEvent } from '../../../common/AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../../common/AnalyticsContext/useAnalytics';
import { StandardModal } from '../../../common/components/StandardModal/StandardModal';
import { HeImgix } from '../../../common/components/images/HeImgix';
import { useMobile } from '../../../common/services/screenSize/ScreenSizeService';
import { PROVIDERS } from '../constants';
import {
  StyledLink,
  Centered,
  View,
  Heading,
  Message,
  SeeDentistButton,
  CancelButton,
  ImageContainer,
} from './InfoModal.styles';

interface Props {
  isOpen: boolean;
  provider?: string;
  onClose?(): void;
  onClickFindProvider?(program?: string): void;
}

export const InfoModal = ({ isOpen, onClose, onClickFindProvider, provider }: Props) => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (isOpen) {
      trackEvent(TrackingEvent.PREFERRED_PROVIDER_DIALOG_POPUP);
    }
  }, [isOpen, trackEvent]);

  const isMobile = useMobile();

  const providerData = PROVIDERS.find(data => data.provider === provider);

  const isBupa = provider === 'BUPA';

  const headerText =
    providerData && (providerData.gapFree || isBupa) ? (
      <ImageContainer>
        <HeImgix
          src={providerData.logo}
          imgixParams={{ fit: 'fill', fill: 'color', 'fill-color': 'fff' }}
          height={isMobile ? 16 : 36}
        />
      </ImageContainer>
    ) : (
      'You might pay less at your insurer’s preferred dentists'
    );

  const bupaMessageText = (
    <>
      <StyledLink
        rel="noopener noreferer"
        to="https://www.bupadental.com.au/no-gap-dental-landing"
        target="_blank"
      >
        Eligible
      </StyledLink>{' '}
      Bupa members receive one
      <div>
        <strong>gap free check-up and clean</strong>
      </div>{' '}
      per year when visiting a Bupa Member First Platinum dentist.
    </>
  );

  const messageText =
    providerData && providerData.gapFree
      ? providerData.description
      : 'Find a dentist that is part of your insurer’s preferred provider scheme to minimise out-of-pocket expenses on eligible treatments.';

  const seeDentistText =
    providerData && providerData.gapFree
      ? `See ${provider} ${providerData.term} providers`
      : `Find ${provider} preferred providers`;

  const bupaSeeDentistButton = (
    <>
      <SeeDentistButton
        onClick={() => onClickFindProvider?.()}
        trackingId={LinkId.PROMINENT_INSURANCE_DIALOG_CONFIRM}
      >
        <Centered>See Bupa Members First dentists</Centered>
      </SeeDentistButton>
      <SeeDentistButton
        onClick={() => onClickFindProvider?.('Bupa Members First Platinum')}
        trackingId={LinkId.PROMINENT_INSURANCE_DIALOG_CONFIRM}
      >
        <Centered>See Bupa Members First Platinum dentists</Centered>
      </SeeDentistButton>
    </>
  );

  return (
    <StandardModal isOpen={isOpen} onRequestClose={onClose} padContainer={false}>
      <View>
        <Heading>{headerText}</Heading>
        <Message>{isBupa ? bupaMessageText : messageText}</Message>
        {isBupa ? (
          bupaSeeDentistButton
        ) : (
          <SeeDentistButton
            onClick={() => onClickFindProvider?.()}
            trackingId={LinkId.PROMINENT_INSURANCE_DIALOG_CONFIRM}
          >
            <Centered>{seeDentistText}</Centered>
          </SeeDentistButton>
        )}
        <CancelButton onClick={onClose}>
          <Centered>Cancel</Centered>
        </CancelButton>
      </View>
    </StandardModal>
  );
};
